<template>
  <div class="page-wrapper generate-reports-page">
    <breadcrumb :items="breadcrumbItems" />

    <div class="page-title">
      <h1 class="mb-1">Generate Reports</h1>
      <!-- <p class="text-muted mb-0">
        Showing report options available to {{ repName }}<span v-if="repCode"> ({{ repCode }})</span>.
        Each rep sees their own list here, enabled by an admin rather than hardcoded per person.
      </p> -->
    </div>

    <div v-if="requiresTarget" class="target-picker-bar">
      <span class="target-picker-label"><i class="fas fa-user-tie"></i>Generate reports for</span>
      <div class="target-picker-options">
        <button
          v-for="option in targetOptions"
          :key="option.agent_id"
          type="button"
          class="target-chip"
          :class="{ active: selectedTargetAgentId === option.agent_id }"
          @click="selectedTargetAgentId = option.agent_id"
        >
          <i class="fas" :class="selectedTargetAgentId === option.agent_id ? 'fa-check-circle' : 'fa-circle'"></i>
          {{ option.name }}
        </button>
      </div>
    </div>

    <div class="reports-card">
      <div v-if="isLoadingCatalog" class="empty-state py-5">
        <spinner-grow />
        <p class="mb-0 mt-2">Loading your report options...</p>
      </div>
      <div v-else-if="!reportGroups.length" class="empty-state py-5">
        <i class="fas fa-folder-open"></i>
        <p class="mb-0">No reports are enabled for your account yet.</p>
      </div>
      <div v-else>
        <div class="catalog-toolbar">
          <div class="search-box">
            <i class="fas fa-search"></i>
            <input type="text" v-model="searchQuery" placeholder="Search reports..." />
          </div>
          <div v-if="!searchQuery && reportGroups.length > 1" class="category-nav">
            <button
              v-for="group in reportGroups"
              :key="group.category"
              type="button"
              class="category-chip"
              :class="{ active: isCategoryExpanded(group.category) }"
              @click="jumpToCategory(group.category)"
            >
              {{ group.category }}
              <span class="chip-count">{{ group.reports.length }}</span>
              <span v-if="selectedCountInCategory(group)" class="chip-dot"></span>
            </button>
          </div>
        </div>

        <div v-if="!filteredGroups.length" class="empty-state py-5">
          <i class="fas fa-search"></i>
          <p class="mb-0">No reports match &quot;{{ searchQuery }}&quot;.</p>
        </div>

        <div v-for="group in filteredGroups" :key="group.category" :id="groupId(group.category)" class="report-group">
          <div class="report-group-header" @click="toggleCategory(group.category)">
            <h5>
              <i class="fas fa-chevron-right chevron" :class="{ expanded: isCategoryExpanded(group.category) }"></i>
              <i :class="['fas', iconForCategory(group.category)]"></i>
              {{ group.category }}
              <span v-if="selectedCountInCategory(group)" class="category-selected-badge">
                {{ selectedCountInCategory(group) }} selected
              </span>
            </h5>
            <button type="button" class="select-all-link" @click.stop="selectAllInCategory(group)">
              {{ isCategoryFullySelected(group) ? "Clear" : "Select all" }}
            </button>
          </div>
          <transition name="collapse">
            <div v-show="isCategoryExpanded(group.category)" class="report-grid">
              <div
                v-for="report in group.reports"
                :key="report.key"
                class="report-card"
                :class="{ selected: isSelected(report.key) }"
                @click="toggleSelection(report.key)"
              >
                <div class="report-card-check">
                  <i class="fas" :class="isSelected(report.key) ? 'fa-check-circle' : 'fa-circle'"></i>
                </div>
                <div class="report-card-body">
                  <div class="report-card-title">
                    {{ report.label }}
                    <span v-if="report.badge" class="badge-pill">{{ report.badge }}</span>
                  </div>
                  <p class="report-card-desc">{{ report.description }}</p>
                  <div v-if="lastRunByKey[report.key]" class="report-card-lastrun">
                    <span class="status-dot" :class="lastRunByKey[report.key].status"></span>
                    <span v-if="lastRunByKey[report.key].status === 'completed'">
                      Last generated {{ timeAgo(lastRunByKey[report.key].completed_at || lastRunByKey[report.key].started_at) }}
                    </span>
                    <span v-else-if="lastRunByKey[report.key].status === 'failed'">Last attempt failed</span>
                    <span v-else>Generating now...</span>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <transition name="slide-up">
      <div v-if="selectedCount" class="selection-bar">
        <div class="selection-bar-inner">
          <div class="tray-chips">
            <span v-for="key in selectedKeys" :key="key" class="tray-chip">
              {{ findReportDefinition(key)?.label || key }}
              <i class="fas fa-times tray-chip-remove" @click="toggleSelection(key)"></i>
            </span>
          </div>
          <div class="tray-actions">
            <button type="button" class="btn-clear" @click="clearSelection">Clear</button>
            <b-button
              variant="primary"
              class="btn-generate"
              :disabled="isGenerating || (requiresTarget && !selectedTargetAgentId)"
              @click="generateSelected"
            >
              <b-spinner small v-if="isGenerating" class="btn-generate-icon" />
              <i v-else class="fas fa-file-export btn-generate-icon"></i>
              <span>Generate</span>
            </b-button>
          </div>
        </div>
      </div>
    </transition>

    <div class="page-title">
      <h2 class="recent-title mb-1">Recently Generated</h2>
    </div>
    <div class="reports-card">
      <div v-if="isLoadingHistory && !historyRecords.length" class="empty-state py-5">
        <spinner-grow />
      </div>
      <div v-else-if="!historyRecords.length" class="empty-state py-5">
        <i class="fas fa-clock"></i>
        <p class="mb-0">No reports generated yet. Select one above to get started.</p>
      </div>
      <template v-else>
        <div v-if="isLoadingHistory" class="history-refreshing">
          <b-spinner small />
          <span>Refreshing...</span>
        </div>
        <transition-group name="fade-in" tag="div" class="history-list">
          <div v-for="row in historyRecords" :key="row.id" class="history-row">
            <div class="history-icon">
              <i class="fas fa-file-excel"></i>
            </div>
            <div class="history-main">
              <div class="history-name">{{ row.file_name }}</div>
              <div class="history-time">
                {{ timeAgo(row.completed_at || row.started_at) }}
                <span v-if="requiresTarget && row.target_agent_name" class="for-agent"> &middot; For {{ row.target_agent_name }}</span>
                <span v-if="row.generated_by"> &middot; Generated by {{ row.generated_by }}</span>
              </div>
              <div class="history-sheets">
                <span v-for="label in row.report_labels" :key="label" class="sheet-chip">
                  <i class="fas fa-table"></i>{{ label }}
                </span>
              </div>
            </div>
            <span class="pill" :class="row.status">
              <i class="fas" :class="statusIcon(row.status)"></i>
              {{ statusLabel(row.status) }}
            </span>
            <div class="history-action">
              <button
                v-if="row.status === 'completed'"
                type="button"
                class="btn-download"
                title="Download"
                @click="downloadRow(row)"
              >
                <i class="fas fa-download"></i>
              </button>
              <span v-else class="text-muted">&mdash;</span>
            </div>
          </div>
        </transition-group>
        <pagination :rows.sync="historyList" :show-page-record="false" />
      </template>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import Breadcrumb from "@/components/ui/Breadcrumb.vue";
import SpinnerGrow from "@/components/ui/SpinnerGrow";
import Api from "@/resource/Api";

const CATEGORY_ICONS = {
  "Downline Composition": "fa-sitemap",
  "Contract Access": "fa-file-contract",
  "Case Activity": "fa-bolt",
  "Plans & Products": "fa-file-invoice-dollar",
  Admin: "fa-user-shield",
};

export default {
  name: "GenerateReports",
  components: { Breadcrumb, SpinnerGrow },
  data() {
    return {
      breadcrumbItems: [{ title: "Generate Reports", active: true }],
      isLoadingCatalog: false,
      isGenerating: false,
      searchQuery: "",
      selectedKeys: [],
      expandedCategories: [],
      reportGroups: [],
      requiresTarget: false,
      targetOptions: [],
      selectedTargetAgentId: null,
      historyList: [],
      isLoadingHistory: false,
    };
  },
  computed: {
    historyRecords() {
      return this.historyList.records || [];
    },
    repName() {
      return this.$store.getters.rep?.name || "your account";
    },
    repCode() {
      return this.$store.getters.repId;
    },
    selectedCount() {
      return this.selectedKeys.length;
    },
    filteredGroups() {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return this.reportGroups;
      return this.reportGroups
        .map((g) => ({
          category: g.category,
          reports: g.reports.filter(
            (r) => r.label.toLowerCase().includes(q) || r.description.toLowerCase().includes(q)
          ),
        }))
        .filter((g) => g.reports.length);
    },
    lastRunByKey() {
      const map = {};
      this.historyRecords.forEach((row) => {
        (row.report_keys || []).forEach((key) => {
          if (!map[key]) map[key] = row;
        });
      });
      return map;
    },
  },
  methods: {
    fetchAvailableReports() {
      this.isLoadingCatalog = true;
      Api.get("v2/reports/available")
        .then((res) => {
          const payload = res.data.data || {};
          const groups = [];
          const byCategory = {};
          (payload.reports || []).forEach((report) => {
            if (!byCategory[report.category]) {
              byCategory[report.category] = { category: report.category, reports: [] };
              groups.push(byCategory[report.category]);
            }
            byCategory[report.category].reports.push(report);
          });
          this.reportGroups = groups;
          this.requiresTarget = !!payload.requires_target;
          this.targetOptions = payload.target_options || [];
          // Only the first category opens by default so a growing catalog
          // never dumps the whole list on screen at once; the rest stay
          // collapsed until a rep opens or searches for them.
          this.expandedCategories = groups.length ? [groups[0].category] : [];
        })
        .catch(() => {
          this.reportGroups = [];
          this.$notify("Unable to load your report options. Please try again.", "error");
        })
        .finally(() => {
          this.isLoadingCatalog = false;
        });
    },
    iconForCategory(category) {
      return CATEGORY_ICONS[category] || "fa-folder";
    },
    groupId(category) {
      return `report-group-${category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
    },
    isCategoryExpanded(category) {
      if (this.searchQuery.trim()) return true;
      return this.expandedCategories.includes(category);
    },
    toggleCategory(category) {
      const idx = this.expandedCategories.indexOf(category);
      if (idx === -1) {
        this.expandedCategories.push(category);
      } else {
        this.expandedCategories.splice(idx, 1);
      }
    },
    jumpToCategory(category) {
      if (!this.expandedCategories.includes(category)) {
        this.expandedCategories.push(category);
      }
      this.$nextTick(() => {
        const el = document.getElementById(this.groupId(category));
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },
    selectedCountInCategory(group) {
      return group.reports.filter((r) => this.isSelected(r.key)).length;
    },
    isSelected(key) {
      return this.selectedKeys.includes(key);
    },
    toggleSelection(key) {
      const idx = this.selectedKeys.indexOf(key);
      if (idx === -1) {
        this.selectedKeys.push(key);
      } else {
        this.selectedKeys.splice(idx, 1);
      }
    },
    isCategoryFullySelected(group) {
      return group.reports.length > 0 && group.reports.every((r) => this.isSelected(r.key));
    },
    selectAllInCategory(group) {
      if (this.isCategoryFullySelected(group)) {
        this.selectedKeys = this.selectedKeys.filter((k) => !group.reports.some((r) => r.key === k));
      } else {
        const missing = group.reports.map((r) => r.key).filter((k) => !this.isSelected(k));
        this.selectedKeys = [...this.selectedKeys, ...missing];
      }
    },
    clearSelection() {
      this.selectedKeys = [];
    },
    findReportDefinition(key) {
      for (const group of this.reportGroups) {
        const found = group.reports.find((r) => r.key === key);
        if (found) return found;
      }
      return null;
    },
    timeAgo(date) {
      return moment(date).fromNow();
    },
    statusIcon(status) {
      if (status === "completed") return "fa-check";
      if (status === "failed") return "fa-times";
      return "fa-spinner fa-spin";
    },
    statusLabel(status) {
      if (status === "completed") return "Completed";
      if (status === "failed") return "Failed";
      return "Processing";
    },
    fetchHistory() {
      this.isLoadingHistory = true;
      Api.get("v2/reports/history")
        .then((res) => {
          this.historyList = res.data;
        })
        .catch(() => {
          this.historyList = [];
        })
        .finally(() => {
          this.isLoadingHistory = false;
        });
    },
    generateSelected() {
      if (!this.selectedKeys.length || this.isGenerating) return;
      if (this.requiresTarget && !this.selectedTargetAgentId) return;

      this.isGenerating = true;
      const keys = [...this.selectedKeys];
      const payload = { keys };
      if (this.requiresTarget) {
        payload.target_agent_id = this.selectedTargetAgentId;
      }

      Api.post("v2/reports/generate", payload)
        .then(() => {
          this.selectedKeys = [];
          this.$notify("Report generated. Find it below in Recently Generated.", "success");
        })
        .catch((err) => {
          this.$notify(err.response?.data?.message || "Failed to generate report.", "error");
        })
        .finally(() => {
          this.isGenerating = false;
          this.fetchHistory();
        });
    },
    downloadRow(row) {
      if (!row.download_url) return;
      window.open(row.download_url, "_blank");
    },
  },
  mounted() {
    this.fetchAvailableReports();
    this.fetchHistory();
    this._tickInterval = setInterval(() => {
      this.$forceUpdate();
    }, 30000);
  },
  beforeDestroy() {
    clearInterval(this._tickInterval);
  },
};
</script>

<style scoped>
.reports-card {
  background: #fff;
  border-radius: 10px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px rgba(23, 43, 63, 0.06);
}

.target-picker-bar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  background: #f0fbfe;
  border: 1px solid #cdeef8;
  border-radius: 10px;
  padding: 0.75rem 1rem;
  margin-bottom: 1.5rem;
}
.target-picker-label {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-weight: 600;
  color: #146a85;
  font-size: 13.5px;
  white-space: nowrap;
}
.target-picker-label i {
  font-size: 12px;
}
.target-picker-options {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.target-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: #fff;
  border: 1px solid #d7e1ea;
  color: #4a5b68;
  font-size: 13px;
  font-weight: 600;
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease;
}
.target-chip i {
  font-size: 13px;
  color: #c3cdd6;
}
.target-chip:hover {
  border-color: #a9dcf2;
}
.target-chip.active {
  background: #03c3ec;
  border-color: #03c3ec;
  color: #fff;
}
.target-chip.active i {
  color: #fff;
}

.catalog-toolbar {
  margin-bottom: 1.25rem;
}
.search-box {
  position: relative;
  max-width: 320px;
}
.search-box i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #a7b4bf;
  font-size: 13px;
}
.search-box input {
  width: 100%;
  padding: 0.55rem 0.75rem 0.55rem 2.1rem;
  border: 1px solid #d7e1ea;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.15s ease;
}
.search-box input:focus {
  border-color: #4db7e9;
}

.category-nav {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
}
.category-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: #f4f7f9;
  border: 1px solid #e3e9ed;
  color: #4a5b68;
  font-size: 12.5px;
  font-weight: 600;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.category-chip:hover {
  border-color: #a9dcf2;
}
.category-chip.active {
  background: #f0fbfe;
  border-color: #03c3ec;
  color: #146a85;
}
.chip-count {
  background: rgba(0, 0, 0, 0.06);
  border-radius: 999px;
  padding: 0 0.4rem;
  font-size: 10.5px;
}
.category-chip.active .chip-count {
  background: rgba(3, 195, 236, 0.18);
}
.chip-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #03c3ec;
}

.report-group {
  margin-bottom: 1.75rem;
}
.report-group:last-of-type {
  margin-bottom: 0;
}
.report-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #d7e1ea;
  padding-bottom: 0.6rem;
  margin-bottom: 1rem;
  cursor: pointer;
}
.report-group-header h5 {
  font-size: 15px;
  font-weight: 700;
  color: #254050;
  margin: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.report-group-header h5 i {
  color: #4db7e9;
  width: 16px;
  text-align: center;
}
.report-group-header h5 i.chevron {
  color: #8b98a3;
  font-size: 11px;
  width: auto;
  transition: transform 0.15s ease;
}
.report-group-header h5 i.chevron.expanded {
  transform: rotate(90deg);
}
.category-selected-badge {
  font-size: 10.5px;
  font-weight: 700;
  color: #146a85;
  background: #f0fbfe;
  border: 1px solid #cdeef8;
  border-radius: 999px;
  padding: 0.15rem 0.55rem;
  text-transform: none;
  letter-spacing: normal;
}
.collapse-enter-active,
.collapse-leave-active {
  transition: opacity 0.15s ease;
}
.collapse-enter,
.collapse-leave-to {
  opacity: 0;
}
.select-all-link {
  background: none;
  border: none;
  color: #03c3ec;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}
.select-all-link:hover {
  text-decoration: underline;
}

.report-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 0.9rem;
}
.report-card {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  border: 1px solid #d7e1ea;
  border-radius: 10px;
  padding: 0.9rem 1rem;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
  background: #fff;
}
.report-card:hover {
  border-color: #a9dcf2;
  box-shadow: 0 2px 8px rgba(23, 43, 63, 0.08);
}
.report-card.selected {
  border-color: #03c3ec;
  background: #f0fbfe;
}
.report-card-check {
  font-size: 18px;
  color: #c3cdd6;
  margin-top: 1px;
}
.report-card.selected .report-card-check {
  color: #03c3ec;
}
.report-card-body {
  flex: 1;
  min-width: 0;
}
.report-card-title {
  font-weight: 600;
  color: #254050;
  font-size: 14px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
}
.badge-pill {
  background: #ffedd5;
  color: #b45309;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  letter-spacing: 0.03em;
}
.report-card-desc {
  font-size: 12.5px;
  color: #717171;
  margin: 0.25rem 0 0;
  line-height: 1.4;
}
.report-card-lastrun {
  font-size: 11.5px;
  color: #8b98a3;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
}
.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  display: inline-block;
  background: #9aa5ad;
  flex-shrink: 0;
}
.status-dot.completed {
  background: #1f9d55;
}
.status-dot.processing {
  background: #e0a428;
  animation: report-pulse 1.4s infinite;
}
.status-dot.failed {
  background: #d64545;
}
@keyframes report-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(224, 164, 40, 0.5);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(224, 164, 40, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(224, 164, 40, 0);
  }
}

.empty-state {
  text-align: center;
  color: #8b98a3;
}
.empty-state i {
  font-size: 28px;
  display: block;
  margin-bottom: 0.5rem;
  color: #c3cdd6;
}

.selection-bar {
  position: sticky;
  bottom: 16px;
  z-index: 20;
  margin-bottom: 1.5rem;
}
.selection-bar-inner {
  background: #fff;
  border: 1px solid #e3e9ed;
  border-radius: 10px;
  padding: 0.6rem 0.7rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.6rem;
  box-shadow: 0 6px 18px rgba(23, 43, 63, 0.08);
}
.tray-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  flex: 1;
  min-width: 0;
  max-height: 60px;
  overflow-y: auto;
}
.tray-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: #f4f7f9;
  border: 1px solid #e3e9ed;
  color: #4a5b68;
  font-size: 12px;
  font-weight: 600;
  padding: 0.25rem 0.35rem 0.25rem 0.6rem;
  border-radius: 999px;
}
.tray-chip-remove {
  cursor: pointer;
  font-size: 9px;
  color: #8b98a3;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease, color 0.15s ease;
}
.tray-chip-remove:hover {
  background: #dfe6ea;
  color: #254050;
}
.tray-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
}
.btn-clear {
  background: transparent;
  border: none;
  color: #5b6b78;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0.5rem 0.6rem;
  border-radius: 8px;
  transition: background-color 0.15s ease, color 0.15s ease;
}
.btn-clear:hover {
  background: #f4f7f9;
  color: #254050;
}
.btn-generate {
  background: linear-gradient(#0cf, #1597b8 49%, #0567bc) !important;
  border: 0 !important;
  border-radius: 8px !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  letter-spacing: 0.01em;
  padding: 0.5rem 1.1rem !important;
  display: inline-flex !important;
  align-items: center;
  gap: 0.5rem;
  transition: box-shadow 0.2s ease;
}
.btn-generate:hover:not(:disabled) {
  box-shadow: 0 8px 18px -8px rgba(5, 103, 188, 0.6);
}
.btn-generate-icon {
  font-size: 13px;
  line-height: 1;
  flex-shrink: 0;
}
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.slide-up-enter,
.slide-up-leave-to {
  transform: translateY(12px);
  opacity: 0;
}

.recent-title {
  font-size: 20px;
  font-weight: bold;
  color: #254050;
}

.history-refreshing {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 12.5px;
  color: #8b98a3;
  margin-bottom: 0.75rem;
}

.history-list {
  display: flex;
  flex-direction: column;
}
.history-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.85rem 0;
  border-bottom: 1px solid #eef2f5;
}
.history-row:last-child {
  border-bottom: none;
}
.history-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #f0fbfe;
  color: #03c3ec;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.history-main {
  flex: 1;
  min-width: 0;
}
.history-name {
  font-weight: 600;
  color: #254050;
  font-size: 14px;
}
.history-time {
  font-size: 12px;
  color: #8b98a3;
}
.history-time .for-agent {
  color: #146a85;
  font-weight: 600;
}
.history-sheets {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.4rem;
}
.sheet-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 11px;
  color: #4a5b68;
  background: #f4f7f9;
  border: 1px solid #e3e9ed;
  border-radius: 6px;
  padding: 0.15rem 0.5rem;
}
.sheet-chip i {
  font-size: 9px;
  color: #4db7e9;
}
.pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 12px;
  font-weight: 600;
  padding: 0.25rem 0.7rem;
  border-radius: 999px;
  text-transform: capitalize;
  flex-shrink: 0;
}
.pill.processing {
  background: #fff4e0;
  color: #b26a00;
}
.pill.completed {
  background: #e5f7ec;
  color: #128a3e;
}
.pill.failed {
  background: #fbe7e7;
  color: #b91c1c;
}
.history-action {
  min-width: 34px;
  display: flex;
  justify-content: flex-end;
}
.btn-download {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: none;
  background: #f0fbfe;
  color: #03c3ec;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, transform 0.15s ease;
}
.btn-download:hover {
  background: #03c3ec;
  color: #fff;
  transform: translateY(-1px);
}
.fade-in-enter-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.fade-in-enter {
  opacity: 0;
  transform: translateY(-6px);
}

@media (max-width: 576px) {
  .history-row {
    flex-wrap: wrap;
  }
  .history-action {
    text-align: left;
    margin-left: 48px;
  }
}
</style>
